import { useState, useEffect } from "react";
import {
  Container,
  Nav,
  Form,
  Row,
  Col,
  ToastContainer,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import SimpleTable from "../../components/atoms/table/simpleTable/simpleTable";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getCustomerRequests,
  toggleRequestStatus,
} from "./SupportManagementApi";
import { setInBrowserStorage } from "../../services/browserStorage";
import "../../components/organisms/SecondTabNavigation.css";
import "../../styles/typography.css";
import "./supportManagement.css";
import PageDescriptionBar from "../../components/atoms/pageDescriptionBar/PageDescriptionBar";

const SupportManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [requests, setRequests] = useState([]);
  const [paginationData, setPaginationData] = useState({});

  const getRequestDateValue = (request) => {
    return (
      request?.requestedDate ||
      request?.requestDate ||
      request?.createdDate ||
      request?.createdOn ||
      request?.createdAt ||
      request?.date ||
      null
    );
  };

  const formatRequestDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");

    // Keep sortable lexical format while still user-readable.
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  };

  const fetchRequests = async (filterText, page, pageSize) => {
    try {
      const validPage = Math.max(1, page);
      const validPageSize = Math.max(1, pageSize);

      const data = await getCustomerRequests(
        validPage,
        validPageSize,
        filterText
      );
      if (data && typeof data === "object") {
        const { totalRecords, pageNumber, pageSize, totalPages } = data;
        setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
        setRequests(Array.isArray(data.results) ? data.results : []);

        const safeCount = Number(totalRecords) || 0;
        setInBrowserStorage("supportRequestCount", safeCount);
        window.dispatchEvent(
          new CustomEvent("support-count-updated", {
            detail: { count: safeCount },
          })
        );
      } else {
        setRequests([]);
        setInBrowserStorage("supportRequestCount", 0);
        window.dispatchEvent(
          new CustomEvent("support-count-updated", {
            detail: { count: 0 },
          })
        );
      }
    } catch (error) {
      console.error("Failed to fetch requests:", error);
      setRequests([]);
      setInBrowserStorage("supportRequestCount", 0);
      window.dispatchEvent(
        new CustomEvent("support-count-updated", {
          detail: { count: 0 },
        })
      );
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchRequests(searchTerm, 1, 25);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleToggleRequest = async (requestId, isUserRegistration = false) => {
    try {
      const result = await toggleRequestStatus(requestId, isUserRegistration);
      const request = requests.find((r) => r.requestId === requestId);
      const newStatus = !request.active;
      toast.success(
        `Request ${newStatus ? "enabled" : "disabled"} successfully`
      );
      await fetchRequests(
        searchTerm,
        paginationData.pageNumber,
        paginationData.pageSize
      );
    } catch (error) {
      console.error("Failed to toggle request:", error);
      toast.error("Failed to update request status. Please try again.");
    }
  };

  const headerColumns = [
    { header: "Date", accessor: "requestDate" },
    { header: "Company", accessor: "company" },
    { header: "Location", accessor: "location" },
    { header: "User Name", accessor: "userName" },
    { header: "Email ID", accessor: "email" },
    { header: "Comments", accessor: "comments" },
    { header: "Action", accessor: "action", disableSort: true },
  ];

  const tabConfig = [
    { label: "Support Management", key: "support-Management" },
  ];

  const formatTableData = () => {
    return requests.map((request) => ({
      requestDate: formatRequestDate(getRequestDateValue(request)),
      company: request.company,
      location: request.location,
      userName: request.userName,
      email: request.email,
      comments: request.comments,
      action: (
        <div className="action-column d-flex align-items-center gap-2">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{(request.active === undefined ? true : request.active) ? "Disable request" : "Enable request"}</Tooltip>}
          >
          <Form.Check
            type="switch"
            id={`switch-${request.requestId}`}
            checked={request.active === undefined ? true : request.active}
            onChange={() => handleToggleRequest(request.requestId, request.userId === 0)}
            className="status-switch active-switch form-check-input-action-col"
          />
          </OverlayTrigger>
        </div>
      ),
    }));
  };

  const paginatedData = formatTableData();

  const renderRow = (row, index) => (
    <tr key={index}>
      {headerColumns.map((column, colIndex) => (
        <td key={colIndex}>{row[column.accessor]}</td>
      ))}
    </tr>
  );

  return (
    <div className="request-management-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <Container fluid className="second-tab-container">
        <Nav
          variant="underline"
          activeKey="user-details"
          className="flex-nowrap nav-underline"
        >
          {tabConfig.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                eventKey={tab.key}
                className="default-font-styles"
                active
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </Container>

      <div className="tab-content-container p-3">
        <div className="user-details-content">
          <PageDescriptionBar items={[
            { type: "badge", badge: "active",   text: "Enabled – Support request is active and visible" },
            { type: "badge", badge: "inactive", text: "Disabled – Support request is hidden from users" },
          ]} />
          <Row className="align-items-center mb-4 search-filter-bar">
            <Col></Col>
            <Col xs="auto" className="d-flex align-items-center gap-2">
              <Form.Control
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </Col>
          </Row>

          <div className="users-table-section mb-4">
            <div className="bg-white rounded shadow-sm p-4">
              <SimpleTable
                columns={headerColumns}
                data={paginatedData}
                customRowRender={renderRow}
                showPagination
                paginationData={paginationData}
                searchValue={searchTerm}
                fetchData={fetchRequests}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportManagement;
